import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Link2, List, ListOrdered, RemoveFormatting, Underline } from "lucide-react";
import { cn } from "@/lib/utils";

export const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    ADD_ATTR: ["target", "rel"],
  });

const RichTextEditor = ({ value, onChange, placeholder, testId, className }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const emitChange = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const exec = (command, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emitChange();
  };

  const addLink = () => {
    const url = window.prompt("https://t.me/...");
    if (url) exec("createLink", url);
  };

  const handleBlur = () => {
    const clean = sanitizeHtml(editorRef.current?.innerHTML || "");
    if (editorRef.current) editorRef.current.innerHTML = clean;
    onChange(clean);
  };

  const tools = [
    { icon: Bold, command: "bold", label: "Bold", id: "bold" },
    { icon: Italic, command: "italic", label: "Italic", id: "italic" },
    { icon: Underline, command: "underline", label: "Underline", id: "underline" },
    { icon: List, command: "insertUnorderedList", label: "Bullet list", id: "list" },
    { icon: ListOrdered, command: "insertOrderedList", label: "Numbered list", id: "ordered-list" },
    { icon: RemoveFormatting, command: "removeFormat", label: "Clear", id: "clear" },
  ];

  return (
    <div className={cn("rounded-md border border-white/15 bg-black overflow-hidden", className)}>
      <div className="flex flex-wrap gap-1 border-b border-white/10 bg-white/[0.03] p-2">
        {tools.map((tool) => (
          <button
            key={tool.id}
            type="button"
            aria-label={tool.label}
            data-testid={`${testId}-${tool.id}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(tool.command)}
            className="p-2 rounded text-zinc-400 hover:text-[#FF1493] hover:bg-white/5 transition-colors"
          >
            <tool.icon className="w-4 h-4" />
          </button>
        ))}
        <button
          type="button"
          aria-label="Add link"
          data-testid={`${testId}-link`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={addLink}
          className="p-2 rounded text-zinc-400 hover:text-[#FF1493] hover:bg-white/5 transition-colors"
        >
          <Link2 className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={handleBlur}
        data-testid={testId}
        data-placeholder={placeholder}
        className="rich-text-editor min-h-[130px] px-3 py-3 text-sm leading-6 text-zinc-200 outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-zinc-600"
      />
    </div>
  );
};

export default RichTextEditor;
